import React, { useEffect, useState } from "react";
import { useWebData } from "../context/WebDataContext"; 
import { db } from "../firebaseConfig";
import { doc, updateDoc, deleteDoc } from "firebase/firestore"; 
import * as XLSX from "xlsx";
import Loader from "../components/Loader";

function Installations() {
  const { installations, loading } = useWebData();

  // 🔥 Filter States
  const [viewMode, setViewMode] = useState('Month'); 
  const [selectedDate, setSelectedDate] = useState(new Date()); 
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear()); 
  const [searchTerm, setSearchTerm] = useState("");
  const [filtered, setFiltered] = useState([]);

  // States
  const [viewData, setViewData] = useState(null);
  const [editData, setEditData] = useState(null);

  // ==========================================
  // 1. UNIVERSAL FILTER LOGIC
  // ==========================================
  useEffect(() => {
    if (!installations) return;

    let result = [...installations];

    // Search Logic
    if (searchTerm) {
        const lower = searchTerm.toLowerCase();
        result = result.filter(i => {
            const dateStr = i.date ? new Date(i.date).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric'
            }).toLowerCase() : "";
            
            const row = `${dateStr} ${i.hospital} ${i.product} ${i.model} ${i.serialNo} ${i.department} ${i.userName} ${i.status}`.toLowerCase();
            return row.includes(lower) || dateStr.includes(lower);
        });
    }

    // Date Logic
    if (viewMode !== 'All') {
        const targetYear = viewMode === 'Year' ? parseInt(selectedYear) : selectedDate.getFullYear();
        const targetMonth = selectedDate.getMonth(); 

        result = result.filter(item => {
            const rawDate = item.date || ""; 
            if (!rawDate) return false;
            let y = 0; let m = -1;
            const d = new Date(rawDate);
            if (!isNaN(d.getTime())) { y = d.getFullYear(); m = d.getMonth(); } 
            else {
                const parts = rawDate.split(/[-/]/);
                if (parts.length === 3) {
                    if (parts[2].length === 4) { y = parseInt(parts[2]); m = parseInt(parts[1]) - 1; } 
                    else if (parts[0].length === 4) { y = parseInt(parts[0]); m = parseInt(parts[1]) - 1; }
                }
            }
            if (viewMode === 'Year') return y === targetYear;
            if (viewMode === 'Month') return y === targetYear && m === targetMonth;
            return true;
        });
    }

    result.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    setFiltered(result);
  }, [installations, searchTerm, viewMode, selectedDate, selectedYear]); 

  // ==========================================
  // 2. EXCEL DOWNLOAD
  // ==========================================
  const downloadExcel = () => {
    const dataToExport = filtered.map(i => ({
        Date: formatDate(i.date),
        Hospital: i.hospital,
        City: i.city || '-',
        Department: i.department || '-',
        Product: i.product, 
        Model: i.model || '-',
        Serial_No: i.serialNo, 
        Engineer: i.userName, 
        Warranty_Exp: i.warrantyExpiry, 
        Status: i.status,
        Notes: i.notes || '-'
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Installations");
    XLSX.writeFile(wb, `Installations_Report_${viewMode}.xlsx`);
  };

  // --- ACTIONS ---
  const handleSaveEdit = async () => {
      if(!editData) return;
      try {
          await updateDoc(doc(db, "installations", editData.id), {
              product: editData.product,
              model: editData.model, 
              serialNo: editData.serialNo,
              warrantyExpiry: editData.warrantyExpiry,
              status: editData.status,
              department: editData.department
          });
          alert("Installation Updated ✅");
          setEditData(null);
      } catch (e) { alert("Error updating"); }
  };

  const handleDelete = async (id) => {
      if(window.confirm("Delete this record?")) await deleteDoc(doc(db, "installations", id));
  };

  // Helpers
  const handleMonthChange = (dir) => {
      const d = new Date(selectedDate);
      d.setMonth(d.getMonth() + dir);
      setSelectedDate(d);
  };

  const getYearOptions = () => {
      const currentY = new Date().getFullYear();
      const years = [];
      for(let y = 2024; y <= currentY + 5; y++) years.push(y);
      return years;
  };

  const formatDate = (dateStr) => {
      if(!dateStr) return "-";
      const date = new Date(dateStr);
      return isNaN(date.getTime()) ? dateStr : date.toLocaleDateString('en-GB');
  };

  return (
    <div style={{padding:"20px", fontFamily:"'Segoe UI', sans-serif", backgroundColor:"#f8f9fa", minHeight:"100vh"}}>
      
      {/* HEADER */}
      <div style={headerCard}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px"}}>
            <h2 style={{margin:0, color:"#27ae60", display:"flex", alignItems:"center"}}>
                ⚙️ Installations <span style={{fontSize:"14px", color:"#777", marginLeft:"10px"}}>({filtered.length})</span>
            </h2>
            <button onClick={downloadExcel} style={btnDownload}>📥 Download Excel</button>
        </div>

        <div style={{display:"flex", gap:"10px", flexWrap:"wrap", alignItems:"center", justifyContent:"space-between"}}>
            <div style={{display:"flex", gap:"5px", backgroundColor:"#eee", padding:"4px", borderRadius:"6px"}}>
                {['Month', 'Year', 'All'].map(mode => (
                    <button key={mode} onClick={()=>setViewMode(mode)} 
                        style={{...toggleBtn, backgroundColor: viewMode===mode ? "white" : "transparent", color: viewMode===mode ? "#333" : "#777", boxShadow: viewMode===mode ? "0 2px 5px rgba(0,0,0,0.1)" : "none"}}>
                        {mode}
                    </button>
                ))}
            </div>

            {viewMode === 'Month' && (
                <div style={{display:"flex", alignItems:"center", gap:"10px", backgroundColor:"white", padding:"5px 15px", borderRadius:"6px", border:"1px solid #ddd"}}>
                    <button onClick={()=>handleMonthChange(-1)} style={navBtn}>◀</button>
                    <span style={{fontWeight:"bold", color:"#333", minWidth:"120px", textAlign:"center"}}>
                        {selectedDate.toLocaleDateString('en-US', {month:'long', year:'numeric'})}
                    </span>
                    <button onClick={()=>handleMonthChange(1)} style={navBtn}>▶</button>
                </div>
            )}

            {viewMode === 'Year' && (
                <select 
                    value={selectedYear} 
                    onChange={(e) => setSelectedYear(e.target.value)}
                    style={{padding:"8px", borderRadius:"6px", border:"1px solid #ddd", fontWeight:"bold", fontSize:"14px", cursor:"pointer"}}
                >
                    {getYearOptions().map(y => (
                        <option key={y} value={y}>{y}</option>
                    ))}
                </select>
            )}

            <input type="text" placeholder="🔍 Search Serial, Hospital..." value={searchTerm} onChange={(e)=>setSearchTerm(e.target.value)} style={searchInput} />
        </div>
      </div>

      {loading ? <Loader /> : (
        <div style={{overflowX:"auto", backgroundColor:"white", borderRadius:"8px", boxShadow:"0 2px 10px rgba(0,0,0,0.05)"}}>
          <table style={tableStyle}>
            <thead>
                <tr style={{backgroundColor:"#27ae60", color:"white", textAlign:"left"}}>
                    <th style={thStyle}>Date</th>
                    <th style={thStyle}>Hospital</th>
                    <th style={thStyle}>Product</th>
                    <th style={thStyle}>Model</th>
                    <th style={thStyle}>Serial No</th>
                    <th style={thStyle}>Engineer</th>
                    <th style={thStyle}>Warranty</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                  <tr><td colSpan="9" style={{padding:"20px", textAlign:"center", color:"#999"}}>No records found.</td></tr>
              ) : filtered.map(item => {
                const today = new Date().toISOString().split('T')[0];
                const isExpired = item.warrantyExpiry && item.warrantyExpiry < today;
                return (
                    <tr key={item.id} style={{borderBottom:"1px solid #eee", height:"45px", backgroundColor: isExpired ? "#ffebee" : "white"}}>
                        <td style={tdStyle}>{formatDate(item.date)}</td>
                        <td style={{...tdStyle, fontWeight:"bold", textAlign:"left"}}>
                            {item.hospital}
                            <div style={{fontSize:"10px", color:"gray"}}>{item.city || '-'}</div>
                        </td>
                        <td style={tdStyle}>{item.product}</td>
                        <td style={tdStyle}>{item.model || '-'}</td>
                        <td style={tdStyle}>{item.serialNo}</td>
                        <td style={tdStyle}>{item.userName}</td>
                        <td style={{...tdStyle, color: isExpired?"red":"green", fontSize:"11px", fontWeight:"bold"}}>{item.warrantyExpiry}</td>
                        <td style={{...tdStyle, fontWeight:"bold", color: item.status==='Completed'?'green':'orange'}}>{item.status}</td>
                        <td style={tdStyle}>
                            <div style={{display:"flex", gap:"3px"}}>
                                <button onClick={() => setViewData(item)} style={btnIcon} title="View">👁️</button>
                                <button onClick={() => setEditData(item)} style={btnIconEdit} title="Edit">✏️</button>
                                <button onClick={() => handleDelete(item.id)} style={btnIconDelete} title="Delete">🗑️</button>
                            </div>
                        </td>
                    </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* VIEW MODAL */}
      {viewData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"15px", borderBottom:"1px solid #eee", paddingBottom:"10px"}}>
                <h3 style={{margin:0, color:"#27ae60"}}>⚙️ Install Details</h3>
                <button onClick={() => setViewData(null)} style={{background:"none", border:"none", fontSize:"18px", cursor:"pointer"}}>✖</button>
            </div>
            
            <div style={{fontSize:"13px", lineHeight:"1.6"}}>
                <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px"}}>
                    <div>
                        <p><strong>📅 Date:</strong> {formatDate(viewData.date)}</p>
                        <p><strong>🏥 Hospital:</strong> {viewData.hospital}</p>
                        <p><strong>📍 City:</strong> {viewData.city || '-'}</p>
                        <p><strong>🏢 Dept:</strong> {viewData.department || '-'}</p>
                    </div>
                    <div>
                        <p><strong>📦 Product:</strong> {viewData.product}</p>
                        <p><strong>🏷️ Model:</strong> {viewData.model || '-'}</p>
                        <p><strong>🔢 Serial:</strong> {viewData.serialNo}</p>
                    </div>
                </div>
                <hr style={{border:"0.5px solid #eee"}}/>
                <p><strong>🛡️ Warranty Exp:</strong> <span style={{color:"red", fontWeight:"bold"}}>{viewData.warrantyExpiry}</span></p>
                <p><strong>🔥 Status:</strong> {viewData.status}</p>
                <div style={{background:"#f9f9f9", padding:"10px", borderRadius:"5px", marginTop:"10px"}}>
                    <p style={{margin:0}}><strong>📝 Notes:</strong> {viewData.notes || "No notes"}</p>
                </div>
                <p style={{marginTop:"10px"}}><strong>👷 Engineer:</strong> {viewData.userName}</p>
            </div>
            <button onClick={() => setViewData(null)} style={btnClose}>Close</button>
          </div>
        </div>
      )}

      {/* EDIT MODAL */}
      {editData && (
        <div style={modalOverlay}>
          <div style={modalContent}>
            <h3 style={{color:"#f39c12", marginTop:0}}>✏️ Edit Installation</h3>
            
            <label style={labelStyle}>Product Name</label>
            <input type="text" value={editData.product} onChange={(e)=>setEditData({...editData, product:e.target.value})} style={modalInput}/>
            
            <div style={{display:"flex", gap:"5px"}}>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Model</label>
                    <input type="text" value={editData.model || ''} onChange={(e)=>setEditData({...editData, model:e.target.value})} style={modalInput}/>
                </div>
                <div style={{flex:1}}>
                    <label style={labelStyle}>Dept</label>
                    <input type="text" value={editData.department || ''} onChange={(e)=>setEditData({...editData, department:e.target.value})} style={modalInput}/>
                </div>
            </div>

            <label style={labelStyle}>Serial No</label>
            <input type="text" value={editData.serialNo} onChange={(e)=>setEditData({...editData, serialNo:e.target.value})} style={modalInput}/>

            <label style={labelStyle}>Warranty Expiry</label>
            <input type="date" value={editData.warrantyExpiry} onChange={(e)=>setEditData({...editData, warrantyExpiry:e.target.value})} style={modalInput}/>

            <label style={labelStyle}>Status</label>
            <select value={editData.status} onChange={(e)=>setEditData({...editData, status:e.target.value})} style={modalInput}>
                <option value="Completed">Completed ✅</option>
                <option value="Pending">Pending ⏳</option>
            </select>

            <div style={{marginTop:"15px", textAlign:"right"}}>
                <button onClick={()=>setEditData(null)} style={btnCancel}>Cancel</button>
                <button onClick={handleSaveEdit} style={btnSave}>Update</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// STYLES
const headerCard={backgroundColor:"white", padding:"20px", borderRadius:"10px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)", marginBottom:"20px"};
const tableStyle={width:"100%", borderCollapse:"collapse", fontSize:"13px"};
const thStyle={padding:"12px 15px", borderBottom:"2px solid #eee", fontSize:"12px", textTransform:"uppercase", letterSpacing:"0.5px"};
const tdStyle={padding:"10px 15px", verticalAlign:"middle"};
const toggleBtn={border:"none", padding:"6px 12px", borderRadius:"5px", cursor:"pointer", fontSize:"13px", fontWeight:"600", transition:"0.2s"};
const navBtn={background:"none", border:"none", cursor:"pointer", fontSize:"16px", color:"#555"};
const searchInput={padding:"8px 12px", border:"1px solid #ddd", borderRadius:"6px", width:"200px", outline:"none"};
const btnDownload={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"5px", cursor:"pointer", fontWeight:"bold", fontSize:"13px"};

const btnIcon={backgroundColor:"#3498db", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconEdit={backgroundColor:"#f39c12", color:"white", border:"none", padding:"5px 8px", borderRadius:"4px", cursor:"pointer", marginRight:"5px"};
const btnIconDelete={backgroundColor:"#fff", color:"#777", border:"1px solid #ddd", padding:"5px 8px", borderRadius:"4px", cursor:"pointer"};

const modalOverlay={position:"fixed", top:0, left:0, width:"100%", height:"100%", background:"rgba(0,0,0,0.5)", display:"flex", justifyContent:"center", alignItems:"center", zIndex:1000};
const modalContent={background:"white", padding:"25px", borderRadius:"12px", width:"420px", boxShadow:"0 10px 30px rgba(0,0,0,0.2)", position:"relative"};
const modalInput={width:"100%", padding:"8px", margin:"5px 0", borderRadius:"4px", border:"1px solid #ccc", boxSizing:"border-box", fontSize:"13px"};
const labelStyle={fontSize:"11px", fontWeight:"bold", color:"#555"};
const btnSave={backgroundColor:"#27ae60", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer", marginLeft:"5px"};
const btnCancel={backgroundColor:"#95a5a6", color:"white", border:"none", padding:"8px 15px", borderRadius:"4px", cursor:"pointer"};
const btnClose={marginTop:"15px", backgroundColor:"#333", color:"white", border:"none", padding:"8px", borderRadius:"4px", width:"100%", cursor:"pointer"};

export default Installations;